import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PartDef, snapToGroup } from '../config/hskTestParts';

interface PartCountConfigProps {
  title: string;
  subtitle: string;
  Icon: React.ComponentType<{ className?: string }>;
  parts: PartDef[];
  config: Record<string, number>;
  onChange: (next: Record<string, number>) => void;
  onStart: () => void;
  startLabel: string;
  error?: string;
}

// Shared "choose how many questions per sub-part" screen used by the standalone
// Listening / Reading / Writing practice tests across HSK levels.
const PartCountConfig: React.FC<PartCountConfigProps> = ({
  title, subtitle, Icon, parts, config, onChange, onStart, startLabel, error,
}) => {
  const navigate = useNavigate();
  const total = parts.reduce((sum, p) => sum + snapToGroup(config[p.key] ?? 0, p.groupSize), 0);

  return (
    <div className="max-w-2xl mx-auto pt-6 pb-12">
      <div className="bg-white rounded-3xl shadow-xl shadow-indigo-500/5 p-8 border border-indigo-50">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-[#4F46E5] flex items-center justify-center">
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#312E81]">{title}</h1>
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {parts.map(p => {
            const grouped = p.groupSize > 1;
            return (
              <div key={p.key} className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                <label className="block text-sm font-bold text-gray-700 mb-2">{p.label} 数量</label>
                <input
                  type="number"
                  min="0"
                  step={p.groupSize}
                  value={config[p.key] ?? 0}
                  onChange={(e) => onChange({ ...config, [p.key]: Math.max(0, Number(e.target.value || 0)) })}
                  onBlur={(e) => onChange({ ...config, [p.key]: snapToGroup(Number(e.target.value || 0), p.groupSize) })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4F46E5] font-medium"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {grouped
                    ? `每 ${p.groupSize} 题为一组，只能填 ${p.groupSize} 的倍数。`
                    : '可填 0，表示这次不出此部分。'}
                </p>
              </div>
            );
          })}
        </div>

        <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 mb-6 flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-bold text-[#312E81]">本次总题数</div>
            <div className="text-xs text-gray-500 mt-1">分组题型会自动按整组取题。</div>
          </div>
          <div className="text-3xl font-bold text-[#4F46E5]">{total}</div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-xl border border-red-200">{error}</div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="sm:flex-1 px-6 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition-colors"
          >
            返回 Dashboard
          </button>
          <button
            onClick={onStart}
            disabled={total <= 0}
            className="sm:flex-1 px-6 py-3 rounded-xl bg-[#4F46E5] text-white font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {startLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartCountConfig;
